import {
  CLIENT_DECLINE_REASONS,
  type ClientDeclineReason,
  ESTIMATE_DECLINE_REASON_LABELS,
} from '@massilia/queries'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@massilia/ui'
import { useState } from 'react'

interface DeclineEstimateDialogProps {
  responseName: string
  isOpen: boolean
  isSubmitting: boolean
  onConfirm: (payload: { comment?: string; reason: ClientDeclineReason }) => void
  onOpenChange: (open: boolean) => void
}

/**
 * Refus d'une proposition. Le motif est obligatoire — c'est lui qui permet à
 * l'agence de revenir avec une offre qui convient, au lieu de constater un
 * silence. Le commentaire ne l'est que pour « Autre », où le motif seul
 * n'apprend rien.
 */
export function DeclineEstimateDialog({
  responseName,
  isOpen,
  isSubmitting,
  onConfirm,
  onOpenChange,
}: DeclineEstimateDialogProps) {
  return (
    <Dialog onOpenChange={onOpenChange} open={isOpen}>
      {/*
       * Remonté à chaque OUVERTURE, par la `key` : Radix ne démonte pas le contenu
       * à la fermeture, il le masque — sans ça, le motif coché et le commentaire de
       * la fois précédente étaient encore là à la réouverture, et repartaient tels
       * quels à l'agence. Un remontage plutôt qu'un effet de remise à zéro, qui
       * ferait un rendu en cascade ; et à l'ouverture plutôt qu'à la fermeture, où
       * le formulaire se viderait sous les yeux du client.
       */}
      <DeclineEstimateDialogBody
        isSubmitting={isSubmitting}
        key={String(isOpen)}
        onConfirm={onConfirm}
        onOpenChange={onOpenChange}
        responseName={responseName}
      />
    </Dialog>
  )
}

function DeclineEstimateDialogBody({
  responseName,
  isSubmitting,
  onConfirm,
  onOpenChange,
}: Omit<DeclineEstimateDialogProps, 'isOpen'>) {
  // Aucun motif pré-coché : « Trop cher » l'était, et un client qui cliquait
  // deux fois sans lire envoyait à l'agence un motif qu'il n'avait pas choisi —
  // sur lequel elle reconstruisait ensuite une offre moins chère.
  const [reason, setReason] = useState<ClientDeclineReason | null>(null)
  const [comment, setComment] = useState('')

  const commentRequired = reason === 'OTHER'
  const canSubmit = reason !== null && (!commentRequired || comment.trim().length > 0)

  return (
    <DialogContent fullScreenOnMobile>
      <DialogHeader>
        <DialogTitle>Refuser « {responseName} »</DialogTitle>
        <DialogDescription>
          Dites-nous pourquoi : nous pourrons vous recontacter avec une proposition plus proche de
          ce que vous cherchez.
        </DialogDescription>
      </DialogHeader>

      <RadioGroup
        className="gap-3"
        onValueChange={(value) => setReason(value as ClientDeclineReason)}
        value={reason ?? ''}
      >
        {CLIENT_DECLINE_REASONS.map((value) => (
          <div className="flex items-center gap-2" key={value}>
            <RadioGroupItem id={`decline-${value}`} value={value} />
            <Label htmlFor={`decline-${value}`}>{ESTIMATE_DECLINE_REASON_LABELS[value]}</Label>
          </div>
        ))}
      </RadioGroup>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="decline-comment">
          Expliquez-nous pourquoi{commentRequired ? '' : ' (facultatif)'}
        </Label>
        <Textarea
          id="decline-comment"
          maxLength={2000}
          onChange={(event) => setComment(event.target.value)}
          placeholder="Ce qui n'allait pas, ce que vous cherchiez…"
          rows={4}
          value={comment}
        />
      </div>

      <DialogFooter>
        <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
          Annuler
        </Button>
        <Button
          className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          disabled={!canSubmit || isSubmitting}
          onClick={() => reason && onConfirm({ comment: comment.trim() || undefined, reason })}
          type="button"
          variant="outline"
        >
          Refuser la proposition
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
